import { DynamicStandaloneArticleHeader } from '@/components/ArticleHeader';
import { DynamicStandaloneBylineProps } from '@/components/Byline';
import { useAppState } from '@/hooks/useAppState';
import { useStable } from '@/hooks/useStable';
import { LabradorComponent } from '@/types/component';
import withDebugMetadata from '@/utils/withDebugMetadata';
import { findBylines } from 'base/components/ArticleHeader';
import { BYLINE_TYPE } from 'base/components/Byline';
import { authorNameToSlug } from 'lib/labrador/author-page/slug';

export const LabradorArticleHeader: LabradorComponent = ({ type, data, meta, descendants }) => {
  const [{ device }] = useAppState();

  const {
    format = 'DD MMM, YYYY',
    publishedDatetime,
    updatedDatetime,
    enabledAuthors,
    photoByline,
    extraBylineLabel,
    extraBylineContent,
  } = data ?? {};

  const labradorImage = descendants?.find(({ type }) => type === 'labradorImage')?.data;

  const { imageUrl, alt, altText, caption, byline, showCaption } = labradorImage ?? {};

  const src = imageUrl?.[device] as string;

  const bylines: DynamicStandaloneBylineProps[] = findBylines(descendants).map(({ data: byline, children }) => {
    const { firstName, lastName } = byline ?? {};

    const { imageUrl } = children?.[0]?.data ?? {};

    const author = [firstName, lastName].filter(Boolean).join(' ');

    return {
      type: BYLINE_TYPE.AUTHOR,
      author,
      image: imageUrl?.[device],
      authorPageSlug: enabledAuthors?.includes?.(author) && authorNameToSlug(author),
    };
  });

  if (photoByline) {
    bylines.push({ type: BYLINE_TYPE.PHOTOGRAPHER, author: photoByline });
  }

  if (extraBylineContent) {
    bylines.push({ type: BYLINE_TYPE.FREE, author: extraBylineContent, text: extraBylineLabel });
  }

  const stableProps = useStable({
    category: { value: data.category, url: data.categoryUrl },
    bylines,
    image: src
      ? { src, alt: alt || altText, byline, caption: showCaption && caption, options: { priority: true } }
      : undefined,
    datetime: { publishedDatetime, updatedDatetime, format },
    tags: data.tags?.map((tag: string) => ({
      value: tag,
    })),
  });

  return (
    <DynamicStandaloneArticleHeader
      headline={data.title}
      description={data.subtitle}
      data-hostpath={data.hostpath}
      data-category={data.category}
      {...stableProps}
      {...withDebugMetadata({ type, meta })}
    />
  );
};

export default LabradorArticleHeader;
